import { useCallback, useMemo } from "react"
import {
  Box,
  useTheme,
  Typography,
  Button,
  Avatar,
  CardMedia,
  ListItemIcon,
  Divider,
} from "@mui/material"
import { deepmerge } from "@mui/utils"
import _ from "lodash"

import { getIcon } from "utils/icon"
import { useVenue } from "providers/venue"
import { useAppUI } from "providers/ui"
import { useTranslation } from "providers/i18n"
import { usePageSideBarLayout } from "providers/ui/hooks"
import { useStepByStep } from "hooks/direction/useStepByStep"
import { useStepByStepMap } from "hooks/direction/useStepByStepMap"
import { useStepTracking } from "hooks/analytics"
import { isValidURL } from "utils/url"
import { sanitizePublicPath } from "providers/venue/utils/fetchUtils"

import { OrdinalSelector } from "components/OrdinalSelector"
import { PageCard } from "components/card"
import {
  ArrowLeftRounded,
  ArrowRightRounded,
  CloseIcon,
  DestinationMarkerIcon,
  OriginMarkerIcon,
  PauseIcon,
  PlayArrowIcon,
  RefreshArrowIcon,
  ShopIcon,
} from "components/icons"
import KeyboardArrowRightRoundedIcon from "@mui/icons-material/KeyboardArrowRightRounded"
import { VerticalStepProgressBar } from "components/Progress/VerticalStepProgressBar"
import { MotionDiv } from "components/motions"
import { SkeletonStepByStepSidebar } from "components/domains/direction/kiosk-h"
import { IContentWithAsideProps } from "layouts/kiosk-h/ContentWithAside"
import { CloseIconButton } from "components/Button"
import { VerticalDirectionImageDialog } from "components/Dialog/VerticalDirectionImageDialog"
import { CoolDownProgress } from "components/CoolDownProgress"
import { BottomSidebarAction } from "components/BottomSidebarAction/BottomSidebarAction"

interface IStepByStepKioskHPageProps {
  Layout?: React.FC<IContentWithAsideProps>
}

export const StepByStepKioskHPage = ({
  Layout = Box,
}: IStepByStepKioskHPageProps) => {
  const { t } = useTranslation()

  const { dataLoaded, isMultiOrdinalVenue } = useVenue()
  const theme = useTheme()
  const { goBack } = useAppUI()

  const {
    isReady,
    currentOrdinal,
    currentLevel,
    currentStep,
    currentStepNumber,
    currentOriginStepLevelName,
    currentDestStepLevelName,
    originName,
    destinationName,
    steps,
    route,
    destinationLogo,
    destinationCategoryIconPath,
    destinationVenueLogo,
    originVenueLogo,
    currentVerticalStepMethod,
    // Auto play
    hasNextStep,
    hasPrevStep,
    isPlaying,
    goToStepByIndex,
    goToNextStep,
    goToPrevStep,
    restartAutoPlay,
    resumeAutoPlay,
    pauseAutoPlay,
    stepAutoplayConfig,
  } = useStepByStep()

  useStepTracking({
    currentStep: currentStepNumber,
    steps,
    enabled: isReady,
  })

  const stepsAvailable = dataLoaded && steps && steps.length > 0

  useStepByStepMap(route, currentStepNumber, {
    offset: {
      bottom: 0,
    },
    enabled: stepsAvailable,
  })

  usePageSideBarLayout({
    sidebar: {
      open: true,
    },
  })

  const prevIcon = useMemo(
    () => getIcon(theme?.assets?.navigation?.prevIcon) || <ArrowLeftRounded />,
    [theme]
  )
  const nextIcon = useMemo(
    () => getIcon(theme?.assets?.navigation?.nextIcon) || <ArrowRightRounded />,
    [theme]
  )

  const playIcon = useMemo(
    () => getIcon(theme?.assets?.navigation?.playIcon) || <PlayArrowIcon />,
    [theme]
  )
  const playAgainIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.playAgainIcon) || <RefreshArrowIcon />,
    [theme]
  )

  const pauseIcon = useMemo(
    () => getIcon(theme?.assets?.navigation?.pauseIcon) || <PauseIcon />,
    [theme]
  )

  const {
    root,
    stepContainer,
    originIcon,
    ordinalSelector,
    actionContainer,
    playerActionContainer,
    secondaryButton,
    primaryButton,
    destinationIcon,
    stepProgressBarContainer,
    stepProgressBar,
    sidebarContent,
    closeButton,
    headerContainer,
    logoContainer,
    occupantLogo,
    venueLogo,
    listItemIcon,
    logoImage,
    titleText,
    routeDescription,
    highlightText,
    divider,
    separator,
    pageCardSideBar,
    cooldownProgress,
    bottomSidebarAction,
  } = theme.components[`AppNavigationStep`]?.styleOverrides || {}

  const sideBarStyleOverrides =
    theme.components[`AppSidebar`]?.styleOverrides || {}
  const { wrapper, body, playerButton } = sideBarStyleOverrides

  const destinationIconComp = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.destination, destinationIcon) || (
        <ShopIcon sx={destinationIcon} />
      ),
    [theme, destinationIcon]
  )

  const closeIcon = useMemo(
    () => getIcon(theme?.assets?.closeIcon) || <CloseIcon />,
    [theme]
  )

  const originMarkerIcon = useMemo(
    () =>
      (getIcon(theme?.assets?.navigation?.origin, originIcon) || (
        <OriginMarkerIcon sx={originIcon} />
      )) as JSX.Element,
    [theme, originIcon]
  )

  const destinationStepIcon = useMemo(
    () =>
      (getIcon(theme?.assets?.directionStep?.destination, destinationIcon) || (
        <DestinationMarkerIcon
          color="error"
          fontSize="small"
          sx={destinationIcon}
        />
      )) as JSX.Element,
    [theme, destinationIcon]
  )

  const separatorIcon = useMemo(
    () =>
      getIcon(theme?.assets?.navigation?.separator, separator) || (
        <KeyboardArrowRightRoundedIcon sx={separator} />
      ),
    [theme, separator]
  )

  const verticalDirectionImageUrl = useMemo(() => {
    const src = _.get(
      theme?.assets?.verticalStep?.[currentVerticalStepMethod],
      "url",
      ""
    )
    return isValidURL(src) ? src : sanitizePublicPath(src)
  }, [currentVerticalStepMethod, theme?.assets?.verticalStep])

  const showVerticalStepDialog = !!verticalDirectionImageUrl

  const handleClose = useCallback(() => {
    goBack()
  }, [goBack])

  return (
    <Layout
      aside={
        <>
          <PageCard sx={pageCardSideBar}>
            <MotionDiv
              sx={deepmerge(wrapper, sidebarContent)}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
            >
              <Box sx={body}>
                {!stepsAvailable ? (
                  <SkeletonStepByStepSidebar />
                ) : (
                  <Box sx={root}>
                    <CloseIconButton
                      sx={closeButton}
                      icon={closeIcon}
                      onClick={handleClose}
                      size="large"
                    />
                    <Box sx={headerContainer}>
                      <Box sx={logoContainer}>
                        <Avatar
                          src={destinationLogo}
                          sx={occupantLogo}
                          variant="logo"
                        >
                          {getIcon(
                            destinationCategoryIconPath,
                            destinationIcon,
                            {
                              imageIconProps: {
                                solidColorFilterId:
                                  "directionOverview_avatar_filter",
                              },
                            }
                          ) || destinationIconComp}
                        </Avatar>
                        <CardMedia
                          component="img"
                          sx={venueLogo}
                          src={destinationVenueLogo}
                        />
                      </Box>
                      <Typography sx={titleText}>
                        {t(destinationName)}
                      </Typography>
                    </Box>
                    <Box sx={routeDescription}>
                      <Typography variant="body1" component="span">
                        {t("from")}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={highlightText}
                        component="span"
                      >
                        {t(originName)}
                      </Typography>
                      {separatorIcon}
                      <Typography
                        variant="body1"
                        sx={highlightText}
                        component="span"
                      >
                        {t(destinationName)}
                      </Typography>
                    </Box>
                    <Divider sx={divider} />
                    <Box sx={stepContainer}>
                      {/* kiosk-h progress bar */}
                      <Box sx={stepProgressBarContainer}>
                        <VerticalStepProgressBar
                          steps={steps}
                          currentStep={currentStepNumber}
                          sx={stepProgressBar}
                          handleClickStep={goToStepByIndex}
                          enableAnimateScroll={true}
                          originStepIcon={originMarkerIcon}
                          originStepImage={
                            originVenueLogo && (
                              <ListItemIcon sx={listItemIcon}>
                                <CardMedia
                                  component="img"
                                  image={originVenueLogo}
                                  sx={logoImage}
                                />
                              </ListItemIcon>
                            )
                          }
                          destinationStepIcon={destinationStepIcon}
                          destinationStepImage={
                            destinationVenueLogo && (
                              <ListItemIcon sx={listItemIcon}>
                                <CardMedia
                                  component="img"
                                  image={destinationVenueLogo}
                                  sx={logoImage}
                                />
                              </ListItemIcon>
                            )
                          }
                        />
                      </Box>
                    </Box>
                    <Box sx={actionContainer}>
                      <Box sx={playerActionContainer}>
                        <Button
                          className="prevButton"
                          variant="outlined"
                          sx={secondaryButton}
                          startIcon={prevIcon}
                          onClick={goToPrevStep}
                          disabled={!hasPrevStep}
                        >
                          <Typography>{t("Previous")}</Typography>
                        </Button>
                        {isPlaying ? (
                          <Button
                            className="playButton"
                            variant="contained"
                            sx={deepmerge(primaryButton, playerButton)}
                            startIcon={pauseIcon}
                            onClick={pauseAutoPlay}
                            disabled={!isPlaying}
                          >
                            <Typography>{t("Stop")}</Typography>
                          </Button>
                        ) : (
                          <Button
                            className="playButton"
                            variant="contained"
                            sx={deepmerge(playerButton, primaryButton)}
                            startIcon={hasNextStep ? playIcon : playAgainIcon}
                            onClick={
                              hasNextStep ? resumeAutoPlay : restartAutoPlay
                            }
                          >
                            <Typography>
                              {hasNextStep ? t("Play") : t("Play Again")}
                            </Typography>
                          </Button>
                        )}
                        <Button
                          className="nextButton"
                          variant="contained"
                          sx={secondaryButton}
                          endIcon={nextIcon}
                          onClick={goToNextStep}
                          disabled={!hasNextStep}
                        >
                          <Typography>{t("Next")}</Typography>
                        </Button>
                      </Box>
                    </Box>
                    {isPlaying && (
                      <CoolDownProgress
                        sx={cooldownProgress}
                        duration={stepAutoplayConfig.delay}
                        repeat={true}
                        reverse={true}
                      />
                    )}
                  </Box>
                )}
              </Box>
            </MotionDiv>
          </PageCard>
          <BottomSidebarAction sx={bottomSidebarAction} />
        </>
      }
    >
      {isMultiOrdinalVenue && (
        <Box sx={ordinalSelector}>
          <OrdinalSelector
            label="Floor"
            levels={currentLevel}
            selectedOrdinal={currentOrdinal}
          />
        </Box>
      )}
      <VerticalDirectionImageDialog
        imageSrc={verticalDirectionImageUrl}
        description={currentStep?.description}
        originLevelName={currentOriginStepLevelName}
        destinationLevelName={currentDestStepLevelName}
        open={showVerticalStepDialog}
      />
    </Layout>
  )
}
